import styled from 'styled-components';
import { maxWidth } from 'config/constants';

export const StyledMachineDashboard = styled.div`
  display: flex;
  flex-direction: column;
  min-height: inherit;
  background-color: ${(props): string => props.theme.kcc.colors.GRAY_50};

  .machine-dashboard__header {
    position: sticky;
    top: 0;
    z-index: 10;
    flex-shrink: 0;
    background-color: ${(props): string => props.theme.kcc.colors.GRAY_50};
  }

  .machine-dashboard__header-content {
    background-color: ${(props): string => props.theme.kcc.colors.GRAY_50};
    padding: ${(props): number => props.theme.kcc.padding.CONTENT_HORIZONTAL}px
      ${(props): number => props.theme.kcc.padding.CONTENT_HORIZONTAL}px 28px;
  }

  .machine-dashboard__body {
    flex: 1;
    min-height: 0;
    background-color: ${(props): string => props.theme.kcc.colors.GRAY_50};
  }

  .machine-dashboard__body-content {
    padding: 0 ${(props): number => props.theme.kcc.padding.CONTENT_HORIZONTAL}px
      ${(props): number => props.theme.kcc.padding.CONTENT_HORIZONTAL}px;
  }

  .machine-dashboard__header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${(props): string => props.theme.kcc.spacing.BASE};
  }

  .machine-dashboard__date-filter-container {
    min-width: 250px;
    max-width: 300px;
    flex-shrink: 0;
    position: relative;
  }

  .machine-dashboard__title {
    flex: 1;
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.HEADING_2}px`};
    font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BLACK};
    line-height: ${(props): number => props.theme.kcc.typography.LINE_HEIGHT.HEADING};
    color: ${(props): string => props.theme.kcc.colors.BLACK};
    text-transform: uppercase;
    margin: 0;
    white-space: nowrap;
  }

  .machine-dashboard__filters-wrapper {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: ${(props): string => props.theme.kcc.spacing.BASE};
  }

  .machine-dashboard__site-filter-trigger {
    min-width: 250px;
    max-width: 250px;
  }

  ${maxWidth.xl} {
    .machine-dashboard__title {
      flex-basis: 100%;
      white-space: normal;
    }
  }

  ${maxWidth.l} {
    .machine-dashboard__filters-wrapper {
      flex-direction: column;
      align-items: stretch;
    }

    .machine-dashboard__site-filter-trigger {
      max-width: 300px;
    }
  }

  .machine-dashboard__date-filter {
    width: 100%;
    max-width: 300px;
    height: ${(props): number => props.theme.kcc.control.HEIGHT}px;

    &--preset {
      .ant-picker-input input {
        color: transparent !important;
        caret-color: transparent;
      }

      .ant-picker-range-separator {
        visibility: hidden;
      }
    }
  }

  .machine-dashboard__date-filter-preset-label {
    position: absolute;
    top: 50%;
    left: ${(props): string => props.theme.kcc.spacing.LG};
    right: 40px;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.LG}px`};
    font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.REGULAR};
    color: ${(props): string => props.theme.kcc.colors.TEXT};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .machine-dashboard__kpi-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${(props): string => props.theme.kcc.spacing.XS};
    background-color: ${(props): string => props.theme.kcc.colors.WHITE};
    border: 2px solid ${(props): string => props.theme.kcc.colors.GRAY_100};
    border-bottom: none;
    padding: ${(props): string => props.theme.kcc.spacing.LG};
    margin-bottom: 0;
  }

  .machine-dashboard__kpi-section-title-group {
    display: flex;
    align-items: center;
    gap: ${(props): string => props.theme.kcc.spacing.XS};
  }

  .machine-dashboard__kpi-section-title {
    font-size: ${(props): string => `${props.theme.kcc.typography.FONT_SIZE.HEADING_5}px`};
    font-weight: ${(props): number => props.theme.kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    margin: 0;
  }

  .machine-dashboard__kpi-section-info-icon {
    display: flex;
    align-items: center;
    width: 24px;
    height: 24px;
    color: ${(props): string => props.theme.kcc.colors.BLACK};
    cursor: pointer;
    flex-shrink: 0;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .machine-dashboard__kpi-section {
    display: flex;
    flex-direction: column;
  }

  .machine-dashboard__kpi-grid {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    overflow: hidden;

    .machine-stats-card:not(:first-child) {
      border-left: none;
    }

    ${maxWidth.l} {
      flex-direction: column;

      .machine-stats-card {
        flex: 0 0 auto;
      }

      .machine-stats-card:not(:first-child) {
        border-left: 2px solid ${(props): string => props.theme.kcc.colors.GRAY_100};
        border-top: none;
      }
    }
  }

  .machine-dashboard__fleet-section {
    margin-top: ${(props): string => props.theme.kcc.spacing.LG};
    display: flex;
    flex-direction: column;
    gap: ${(props): string => props.theme.kcc.spacing.LG};
  }

  .machine-dashboard__fleet-chart-col {
    display: flex;
    flex-direction: column;
    height: 460px;

    .ant-spin-nested-loading {
      flex: 1;
      height: 100%;
    }

    .ant-spin-container {
      height: 100%;
      display: flex;
      flex-direction: column;

      > * {
        flex: 1;
        height: 100%;
      }
    }

    /* Machine Utilisation: grow with long i18n alert text so Device Table link
       is never clipped (FR/IT/PL custom-range alerts wrap to ~3 lines). */
    &--utilisation {
      height: auto;
      min-height: 460px;

      .machine-stats-card {
        height: 100%;
        max-height: none;
      }

      .doughnut-kpi-card__chart-wrapper {
        flex: 0 1 auto;
        width: 100%;
        max-width: 200px;
        max-height: 200px;
        align-self: center;
        min-height: 0;
      }

      .doughnut-kpi-card__legend,
      .doughnut-kpi-card__alert {
        flex-shrink: 0;
      }
    }
  }

  .machine-dashboard__site-chart-col {
    .site-performance-chart {
      min-height: unset;
    }
  }
`;
